import React from 'react';
import { Container, Typography, Box, Paper, Button, Divider } from '@mui/material';
import { motion } from 'framer-motion';
import LightbulbIcon from '@mui/icons-material/Lightbulb';
import ScienceIcon from '@mui/icons-material/Science';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import BuildIcon from '@mui/icons-material/Build';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import PsychologyIcon from '@mui/icons-material/Psychology';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';

const ResearchDevelopment: React.FC = () => {
  // Research focus areas
  const researchAreas = [
    {
      title: 'Advanced Software Technologies',
      description: 'Exploration of cutting-edge software design methodologies and platforms.',
      icon: <ScienceIcon fontSize="large" color="primary" />
    },
    {
      title: 'Market Analysis & Innovation',
      description: 'Identifying emerging market demands and industry trends to develop innovative solutions.',
      icon: <TrendingUpIcon fontSize="large" color="primary" />
    },
    {
      title: 'Product Development',
      description: 'Creating high-tech, high-quality products through research-backed approaches.',
      icon: <BuildIcon fontSize="large" color="primary" />
    },
    {
      title: 'Future Technologies',
      description: 'Researching and adopting next-generation technologies to stay ahead of the curve.',
      icon: <RocketLaunchIcon fontSize="large" color="primary" />
    }
  ];

  // R&D process steps
  const processSteps = [
    {
      title: 'Market Research',
      description: 'Analyzing market demands, industry trends, and competitor landscape.'
    },
    {
      title: 'Concept Development',
      description: 'Creating innovative concepts based on research findings and stakeholder requirements.'
    },
    {
      title: 'Prototype Development',
      description: 'Building functional prototypes to test and validate concepts.'
    },
    {
      title: 'Testing & Validation',
      description: 'Rigorous testing to ensure quality, performance, and market fit.'
    },
    {
      title: 'Refinement & Optimization',
      description: 'Enhancing solutions based on test results and feedback.'
    },
    {
      title: 'Implementation & Deployment',
      description: 'Launching products and solutions with comprehensive support systems.'
    }
  ];

  return (
    <Box sx={{ overflow: 'hidden' }}>
      {/* Hero Section */}
      <Box
        component={motion.div}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5 }}
        sx={{
          background: 'linear-gradient(135deg, #0c2340 0%, #1a365d 100%)',
          color: 'white',
          position: 'relative',
          overflow: 'hidden',
          minHeight: '100dvh',
          display: 'flex',
          alignItems: 'center'
        }}
      >
        <Container maxWidth="lg">
          <Box sx={{ 
            display: 'flex', 
            flexWrap: 'wrap', 
            gap: 4, 
            alignItems: 'center'
          }}>
            <Box sx={{ flex: { xs: '1 1 100%', md: '1 1 calc(50% - 16px)' } }}>
              <motion.div
                initial={{ opacity: 0, x: -50 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8, delay: 0.2 }}
              >
                <Typography variant="h3" component="h1" fontWeight="bold" gutterBottom>
                  Research & Development
                </Typography>
                <Typography variant="h6" sx={{ mb: 4, fontWeight: 'normal' }}>
                  Innovative solutions driven by research and cutting-edge technologies
                </Typography>
                <Button
                  variant="contained"
                  size="large"
                  component="a"
                  href="/contact"
                  endIcon={<ArrowForwardIcon />}
                  sx={{
                    py: 1.5,
                    px: 4,
                    borderRadius: '50px',
                    background: 'linear-gradient(90deg, #1976d2, #2196f3)',
                    '&:hover': {
                      background: 'linear-gradient(90deg, #1565c0, #1976d2)',
                      transform: 'translateY(-2px)',
                      boxShadow: '0 8px 20px rgba(0,0,0,0.3)'
                    },
                    transition: 'all 0.3s ease'
                  }}
                >
                  Contact Us
                </Button>
              </motion.div>
            </Box>
            <Box sx={{ flex: { xs: '1 1 100%', md: '1 1 calc(50% - 16px)' } }}>
              <Box
                component={motion.div}
                initial={{ opacity: 0, y: 50 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.4 }}
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                }}
              >
                <LightbulbIcon sx={{ fontSize: 180, opacity: 0.8 }} />
              </Box>
            </Box>
          </Box>
        </Container>
      </Box>

      {/* Main Content Section */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Box sx={{ 
          display: 'flex', 
          flexWrap: 'wrap', 
          gap: 6 
        }}>
          <Box sx={{ flex: { xs: '1 1 100%', md: '1 1 calc(50% - 24px)' } }}>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <Typography variant="h4" component="h2" fontWeight="bold" gutterBottom color="primary">
                Our Research & Development
              </Typography>
              <Divider sx={{ mb: 3, width: '70px', borderWidth: 2, borderColor: 'primary.main' }} />
              <Typography variant="body1" paragraph sx={{ mb: 4 }}>
                Our primary goal is to exploit potential market demand and industry trends. We do it by developing high-tech and high-quality products. We take the challenge by creating new software value through research and development and adapting advanced software platforms. We aim at becoming a research backed software development organization by strengthening our capabilities and by applying state-of-the-art software design technologies.
              </Typography>

              <Typography variant="body1" paragraph>
                At Right Track Technologies, we believe that continuous research and development is essential to remain competitive and deliver exceptional solutions to our clients. Our dedicated R&D team stays ahead of emerging technologies and trends, ensuring that we can offer innovative and forward-thinking solutions.
              </Typography>
              
              <Typography variant="h6" fontWeight="bold" sx={{ mt: 4, mb: 2 }}>
                Our Objectives
              </Typography>
              
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 3 }}>
                {[
                  'Create innovative software solutions that address real-world challenges',
                  'Identify and exploit emerging market opportunities',
                  'Enhance existing products and services through continuous improvement',
                  'Build a foundation for sustainable technological advancement',
                  'Develop intellectual property that creates long-term value'
                ].map((objective, index) => (
                  <Box 
                    key={index} 
                    sx={{ display: 'flex', alignItems: 'center', gap: 2 }}
                    component={motion.div}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.4, delay: index * 0.1 }}
                  >
                    <CheckCircleOutlineIcon color="primary" />
                    <Typography variant="body1">{objective}</Typography>
                  </Box>
                ))}
              </Box>
            </motion.div>
          </Box>

          <Box sx={{ flex: { xs: '1 1 100%', md: '1 1 calc(50% - 24px)' } }}>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <Box
                component="img"
                src="https://images.unsplash.com/photo-1507668077129-56e32842fceb?ixlib=rb-1.2.1&auto=format&fit=crop&w=1374&q=80"
                alt="Research and Development"
                sx={{
                  width: '100%',
                  height: 'auto',
                  borderRadius: '16px',
                  boxShadow: '0 20px 40px rgba(0,0,0,0.1)',
                }}
              />
            </motion.div>
          </Box>
        </Box>
      </Container>

      {/* Research Areas Section */}
      <Box sx={{ py: 10, background: '#f5f7fa' }}>
        <Container maxWidth="lg">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <Typography
              variant="h4"
              component="h2"
              textAlign="center"
              fontWeight="bold"
              gutterBottom
              sx={{ mb: 2 }}
            >
              Our Research Focus Areas
            </Typography>
            <Typography
              variant="subtitle1"
              color="text.secondary"
              textAlign="center"
              sx={{ mb: 6, maxWidth: '700px', mx: 'auto' }}
            >
              Driving innovation through targeted research in key areas
            </Typography>
          </motion.div>

          <Box sx={{ 
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              sm: 'repeat(2, 1fr)',
              md: 'repeat(4, 1fr)'
            },
            gap: 4
          }}>
            {researchAreas.map((area, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <Paper
                  elevation={0}
                  sx={{
                    p: 4,
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    textAlign: 'center',
                    borderRadius: '16px',
                    boxShadow: '0 10px 30px rgba(0,0,0,0.08)',
                    transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-10px)',
                      boxShadow: '0 15px 35px rgba(0,0,0,0.1)',
                    }
                  }}
                >
                  <Box sx={{ mb: 2 }}>{area.icon}</Box>
                  <Typography
                    variant="h6"
                    component="h3"
                    gutterBottom
                    fontWeight="bold"
                    color="primary"
                  >
                    {area.title}
                  </Typography>
                  <Typography variant="body2">
                    {area.description}
                  </Typography>
                </Paper>
              </motion.div>
            ))}
          </Box>
        </Container>
      </Box>

      {/* R&D Process Section */}
      <Container maxWidth="lg" sx={{ py: 10 }}>
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
        >
          <Typography
            variant="h4"
            component="h2"
            textAlign="center"
            fontWeight="bold"
            gutterBottom
            sx={{ mb: 2 }}
          >
            Our R&D Process
          </Typography>
          <Typography
            variant="subtitle1"
            color="text.secondary"
            textAlign="center"
            sx={{ mb: 6, maxWidth: '700px', mx: 'auto' }}
          >
            A structured approach to innovation and product development
          </Typography>
        </motion.div>

        <Box sx={{ 
          display: 'flex',
          flexDirection: 'column',
          gap: 4
        }}>
          {processSteps.map((step, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, x: index % 2 === 0 ? -30 : 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
            >
              <Paper
                elevation={0}
                sx={{
                  p: 4,
                  borderRadius: '16px',
                  boxShadow: '0 10px 30px rgba(0,0,0,0.08)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 4,
                  flexDirection: { xs: 'column', sm: 'row' },
                }}
              >
                <Box
                  sx={{
                    width: 70,
                    height: 70,
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: 'linear-gradient(135deg, #1976d2 0%, #2196f3 100%)',
                    color: 'white',
                    fontSize: 24,
                    fontWeight: 'bold',
                    flexShrink: 0,
                  }}
                >
                  {index + 1}
                </Box>
                <Box>
                  <Typography variant="h6" component="h3" fontWeight="bold" color="primary" gutterBottom>
                    {step.title}
                  </Typography>
                  <Typography variant="body1">
                    {step.description}
                  </Typography>
                </Box>
              </Paper>
            </motion.div>
          ))}
        </Box>
      </Container>

      {/* Innovation Approach Section */}
      <Box sx={{ py: 10, background: '#f5f7fa' }}>
        <Container maxWidth="lg">
          <Box sx={{ 
            display: 'flex', 
            flexDirection: { xs: 'column', md: 'row' },
            alignItems: 'center',
            gap: 6
          }}>
            <Box sx={{ flex: { xs: '1 1 100%', md: '1 1 calc(40% - 24px)' } }}>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
              >
                <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                  <PsychologyIcon sx={{ fontSize: 200, color: '#1976d2', opacity: 0.8 }} />
                </Box>
              </motion.div>
            </Box>
            
            <Box sx={{ flex: { xs: '1 1 100%', md: '1 1 calc(60% - 24px)' } }}>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.2 }}
              >
                <Typography variant="h4" component="h2" fontWeight="bold" gutterBottom color="primary">
                  Our Innovation Approach
                </Typography>
                <Divider sx={{ mb: 3, width: '70px', borderWidth: 2, borderColor: 'primary.main' }} />
                <Typography variant="body1" paragraph>
                  At Right Track Technologies, we believe in a holistic approach to innovation. We combine market research, technological expertise, and creative thinking to deliver solutions that are not just technologically advanced but also commercially viable and user-friendly.
                </Typography>
                <Typography variant="body1" paragraph>
                  Our innovation approach includes:
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
                  {[
                    'Continuous exploration of emerging technologies and methodologies',
                    'Cross-disciplinary collaboration and knowledge sharing',
                    'Iterative design and development processes',
                    'User-centered research and testing',
                    'Strategic partnerships with industry leaders and academic institutions'
                  ].map((item, index) => (
                    <Box 
                      key={index} 
                      sx={{ display: 'flex', alignItems: 'center', gap: 2 }}
                      component={motion.div}
                      initial={{ opacity: 0, x: -20 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.4, delay: index * 0.1 }}
                    >
                      <CheckCircleOutlineIcon color="primary" />
                      <Typography variant="body1">{item}</Typography>
                    </Box>
                  ))}
                </Box>
              </motion.div>
            </Box>
          </Box>
        </Container>
      </Box>

      {/* Call to Action Section */}
      <Box sx={{ py: 10 }}>
        <Container maxWidth="md">
          <Paper
            component={motion.div}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            elevation={0}
            sx={{
              p: { xs: 4, md: 6 },
              borderRadius: '16px',
              background: 'linear-gradient(135deg, #0c2340 0%, #1a365d 100%)',
              color: 'white',
              textAlign: 'center',
              boxShadow: '0 20px 40px rgba(0,0,0,0.2)',
            }}
          >
            <Typography variant="h4" component="h2" fontWeight="bold" gutterBottom>
              Ready to innovate with us?
            </Typography>
            <Typography variant="subtitle1" sx={{ mb: 4, opacity: 0.9, maxWidth: '600px', mx: 'auto' }}>
              Partner with our research and development team to create cutting-edge solutions for your business
            </Typography>
            <Button
              variant="contained"
              size="large"
              component="a"
              href="/contact"
              sx={{
                py: 1.5,
                px: 4,
                borderRadius: '50px',
                background: 'white',
                color: '#0c2340',
                '&:hover': {
                  background: 'rgba(255,255,255,0.9)',
                  transform: 'translateY(-2px)',
                  boxShadow: '0 8px 20px rgba(0,0,0,0.3)'
                },
                transition: 'all 0.3s ease'
              }}
            >
              Get in Touch
            </Button>
          </Paper>
        </Container>
      </Box>
    </Box>
  );
};

export default ResearchDevelopment; 